/**
 * The in-raid zone map (M).
 *
 * A full-screen Requiem plate: the Blender top-down render of the current map (`zoneMapFor` in the
 * shared package picks the rect, the plate URL and the area names for whichever `MapDef.id` is
 * live — the Spine's plate is rendered over exactly `SPINE_ZONE`, see
 * `tools/assetgen/blender/spine_region.py`'s `PLATE_RECT`, and Lanternfall/the Vigil Downs follow
 * the same contract with their own region scripts), a 20 m grid over it, that map's area names in
 * Bebas caps, and every live thing the client already knows about drawn on top.
 *
 * Three rules shape the whole file:
 *
 *  1. **One canvas, 10 Hz.** Every layer — plate, grid, names, hatch, markers — is one redraw of
 *     one canvas on a 100 ms accumulator, not a DOM node per marker on a 60 Hz frame. The frame
 *     loop calls `update(dt)`; a frame that is not a redraw frame touches nothing at all. The only
 *     DOM the panel owns is its frame, its deck tabs, two buttons and a tooltip.
 *  2. **It draws what the client already has.** No new protocol, no new fetch, no new pipeline.
 *     Allies come from `RemoteManager`, Hymns from `EntityManager`, extracts from the last
 *     `ExtractSnap` list, the quest area from the last `S2C.QuestState`, and the roaming Verger's
 *     ground from parsing the room's own chat line (there is no field for it — see
 *     `roamRegionForNotice` in the shared package).
 *  3. **The transform is shared and tested.** `zoneToMap`/`mapToZone`/`zoneZoomAbout` live in
 *     `@stillsun/shared` with a unit test, because a map that is a pixel out at the rim is a map
 *     that lies about where the extract is.
 *
 * Colours are the raid's four voices and nothing else: mint = you and your objectives, cyan =
 * allies, magenta = Hymns (and the storm, which is the Choir's weather), red = your own bag on
 * the ground. Everything else is plum, wine and lilac. They are read off the live CSS custom
 * properties at open time, so re-skinning index.html re-skins the canvas too.
 */

import {
  SPINE_APRON_RECT,
  SPINE_CHAIN_RECT,
  SPINE_DECKS,
  SPINE_DECK_SLAB_M,
  SPINE_HULL_RECT,
  SPINE_PIT_RECT,
  SPINE_RIDGE_HATCH_RECT,
  SPINE_SHAFT_RECT,
  SPINE_STAIR_RECT,
  SPINE_ZONE,
  SPINE_ZONE_AREAS,
  ZONE_GRID_M,
  clampZonePan,
  inSpineHull,
  roamRegionForNotice,
  spineDeckAt,
  zoneFit,
  zoneMapFor,
  zoneToMap,
  zoneZoomAbout,
} from '@stillsun/shared';
import type { ExtractSnap } from '../render/beacons.js';
import type {
  Aabb,
  MapDef,
  Vec3,
  ZoneArea,
  ZoneFit,
  ZoneRect,
  ZoneRoamRegion,
  ZoneView,
} from '@stillsun/shared';
import './zonemap.css';

/** Redraw cadence. 10 Hz is four frames of slack at 60 fps and reads as live on a map. */
const REDRAW_MS = 100;

/** How far a Hymn can be and still be worth a mark. Past this it is a rumour, not a position. */
const HYMN_RANGE_M = 120;

/** Pixel radius a marker answers a hover from. Bigger than the glyph: a map is read at a glance. */
const HOVER_R = 13;

/** What "centre on me" zooms to from a fitted view — see `centreOnMe`. */
const CENTRE_ZOOM = 2;

/** Cap the backing store: a 2x canvas at 1240x780 is already 4.4 MPx to clear ten times a second. */
const MAX_DPR = 2;

/** One live remote, as `RemoteManager.compassTargets` fills it. */
export interface ZoneTarget {
  id: number;
  pos: Vec3;
}

/** One live Hymn, as `EntityManager.revealPoints` fills it. */
export interface ZoneRevealPoint {
  pos: Vec3;
  height: number;
  friendly: boolean;
}

/** The quest area the tracker is pointing at, flattened out of the last `S2C.QuestState`. */
export interface ZoneQuestArea {
  name: string;
  x: number;
  y: number;
  z: number;
  radius: number;
}

/** The person a finished rung is waiting on ("RETURN TO …"), and where they stand. */
export interface ZoneHandIn {
  name: string;
  x: number;
  y: number;
  z: number;
}

/** Everything the panel pulls, rather than being pushed, because it changes every frame. */
export interface ZoneMapHost {
  /** The local player's feet and camera yaw, or null when there is no raid to draw. */
  viewer: () => { pos: Vec3; yaw: number } | null;
  /** Fills `out` with every live remote and returns how many are valid. */
  allies: (out: ZoneTarget[]) => number;
  /** A remote's display name. */
  allyName: (id: number) => string;
  /** Fills `out` with every live Hymn and returns how many are valid. */
  hymns: (out: ZoneRevealPoint[]) => number;
  /** Open/closed edge: the host unions this with its other panels (pointer lock, combat). */
  onToggle: (open: boolean) => void;
}

/** One map's plate image, cached by URL so a load or a 404 for one map cannot touch another. */
interface ZonePlate {
  img: HTMLImageElement;
  ready: boolean;
}

/** A marker the pointer can land on. Rebuilt every redraw; never allocated per frame. */
interface ZoneHit {
  x: number;
  y: number;
  text: string;
}

/** A drawn extract, flattened out of the last `ExtractSnap` list against the map's own names. */
interface ZoneExit {
  id: number;
  name: string;
  x: number;
  y: number;
  z: number;
  active: boolean;
}

/** One thing worth marking on a deck plan that is not a wall. */
interface DeckFeature {
  rect: ZoneRect;
  label: string;
  tip: string;
  /** Which of SPINE_DECKS it is drawn on. */
  decks: readonly number[];
}

/**
 * The routes between decks, as the plan marks them. Rects are the shared ones `maps/spine.ts`
 * builds the geometry from, so a route that moves in the map data moves here with it.
 */
const DECK_FEATURES: readonly DeckFeature[] = [
  { rect: SPINE_PIT_RECT, label: 'DROP', tip: 'The black drop — 6 m to the pit floor', decks: [0] },
  {
    rect: SPINE_CHAIN_RECT,
    label: 'CLIMB',
    tip: 'Collapsed floor — mantle up to Deck 1',
    decks: [0, 1],
  },
  { rect: SPINE_STAIR_RECT, label: 'STAIR', tip: 'Stair, Deck 0 to Deck 1', decks: [0, 1] },
  {
    rect: SPINE_SHAFT_RECT,
    label: 'SHAFT',
    tip: 'Lift shaft — ledges north, open drop south',
    decks: [1, 2],
  },
  {
    rect: SPINE_RIDGE_HATCH_RECT,
    label: 'HATCH',
    tip: 'Roof hatch — the stair onto the dorsal ridge',
    decks: [2, 3],
  },
];

/** The interior view's rect: the apron with a little air around it so the hull is not cropped. */
const DECK_VIEW_RECT: ZoneRect = {
  x0: SPINE_APRON_RECT.x0 - 5,
  x1: SPINE_APRON_RECT.x1 + 5,
  z0: SPINE_APRON_RECT.z0 - 5,
  z1: SPINE_APRON_RECT.z1 + 5,
};

/** Canvas colours, read off the live Requiem tokens so this file hard-codes no palette. */
interface ZoneInk {
  plum: string;
  wine: string;
  moon: string;
  lilac: string;
  lilacDim: string;
  mint: string;
  cyan: string;
  magenta: string;
  red: string;
  rule: string;
}

const FALLBACK_INK: ZoneInk = {
  plum: '#0e0a1c',
  wine: '#3b2a72',
  moon: '#f2effa',
  lilac: '#c9bcff',
  lilacDim: '#8a7cc2',
  mint: '#7dffb8',
  cyan: '#6ee7ff',
  magenta: '#ff6ad5',
  red: '#ff3b5c',
  rule: 'rgba(201, 188, 255, 0.22)',
};

function clockOf(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  return `${String(Math.floor(total / 60))}:${String(total % 60).padStart(2, '0')}`;
}

export class ZoneMap {
  private readonly host: ZoneMapHost;
  private readonly root: HTMLDivElement;
  private readonly sashEl: HTMLDivElement;
  private readonly subEl: HTMLDivElement;
  private readonly stage: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly tipEl: HTMLDivElement;
  private readonly deckStrip: HTMLDivElement;
  private readonly deckBtns: HTMLButtonElement[] = [];
  private readonly zoomEl: HTMLSpanElement;

  private ink: ZoneInk = FALLBACK_INK;
  /** One entry per plate URL ever requested, so a map you have already visited never re-fetches
   *  and a 404 on one map's plate can never be read as a state for another's. */
  private readonly plates = new Map<string, ZonePlate>();
  /** The current map's plate URL, or null when it has none (no entry, or an entry with no plate). */
  private plateUrl: string | null = null;

  private opened = false;
  private redrawAcc = 0;
  private dirty = true;

  // --- what the host has pushed --------------------------------------------------------------
  private map: MapDef | null = null;
  /** The rect the overview fits: the current map's plate rect, or its own computed bounds. */
  private zoneRect: ZoneRect = SPINE_ZONE;
  /** The area names printed on the overview, for whichever map is current. */
  private areas: readonly ZoneArea[] = SPINE_ZONE_AREAS;
  private hasPlate = false;
  /** Kit boxes per deck band, built once per map — never re-filtered on a redraw. */
  private deckBoxes: Aabb[][] = [[], [], [], []];
  private exits: ZoneExit[] = [];
  private questArea: ZoneQuestArea | null = null;
  private handIn: ZoneHandIn | null = null;
  private bag: Vec3 | null = null;
  private roam: ZoneRoamRegion | null = null;
  private lastSub = '';

  // --- view state ----------------------------------------------------------------------------
  private zoom = 1;
  private panX = 0;
  private panY = 0;
  private stageW = 0;
  private stageH = 0;
  private dpr = 1;
  /** Which deck the tabs are showing; null = follow the deck the player is standing on. */
  private pickedDeck: number | null = null;
  private interior = false;
  private shownDeck = 0;

  // --- scratch (never reallocated on a redraw) ------------------------------------------------
  private readonly allyBuf: ZoneTarget[] = [];
  private readonly hymnBuf: ZoneRevealPoint[] = [];
  private readonly hits: ZoneHit[] = [];
  private hitCount = 0;

  // --- pointer -------------------------------------------------------------------------------
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private dragFrom: { x: number; y: number; panX: number; panY: number } | null = null;
  private pinchDist = 0;
  private pinchZoom = 1;
  private hoverX = -1;
  private hoverY = -1;
  /** Where the fitted rect landed this redraw, so every marker can be culled against it. */
  private fit: ZoneFit = { scale: 0, x: 0, y: 0, w: 0, h: 0 };

  /** Screenshot/console override for the viewer pose, mirroring `Hud.forceHaloState`. */
  private forced: { pos: Vec3; yaw: number } | null = null;

  constructor(host: ZoneMapHost) {
    this.host = host;

    const root = document.createElement('div');
    root.id = 'zonemap';
    root.innerHTML = `
      <div class="zm-scrim"></div>
      <div class="zm-frame">
        <div class="zm-head">
          <div class="zm-sash">THE SPINE</div>
          <div class="zm-sub"></div>
          <button type="button" class="zm-close" title="Close the map (M or ESC)">ESC</button>
        </div>
        <div class="zm-stage">
          <canvas class="zm-canvas"></canvas>
          <div class="zm-legend">
            <span class="me">you</span><span class="ally">allies</span>
            <span class="hymn">hymns</span><span class="bag">your bag</span>
          </div>
          <div class="zm-decks"></div>
          <div class="zm-tools"><span class="zm-zoom">1.0x</span></div>
          <div class="zm-tip"></div>
        </div>
      </div>`;
    document.body.appendChild(root);
    this.root = root;
    this.sashEl = root.querySelector('.zm-sash')!;
    this.subEl = root.querySelector('.zm-sub')!;
    this.stage = root.querySelector('.zm-stage')!;
    this.canvas = root.querySelector('canvas')!;
    this.ctx = this.canvas.getContext('2d');
    this.tipEl = root.querySelector('.zm-tip')!;
    this.deckStrip = root.querySelector('.zm-decks')!;
    this.zoomEl = root.querySelector('.zm-zoom')!;

    root.querySelector<HTMLButtonElement>('.zm-close')?.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.close();
    });

    SPINE_DECKS.forEach((deck, i) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'zm-deck';
      btn.textContent = deck.tab;
      btn.title = deck.name;
      btn.addEventListener('pointerdown', (ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        this.pickedDeck = i;
        this.dirty = true;
        this.paintDeckStrip();
      });
      this.deckStrip.append(btn);
      this.deckBtns.push(btn);
    });

    const tools = root.querySelector('.zm-tools');
    const centre = document.createElement('button');
    centre.type = 'button';
    centre.className = 'zm-centre';
    centre.textContent = 'CENTRE ON ME';
    centre.title = 'Put yourself back in the middle';
    centre.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      this.centreOnMe();
    });
    tools?.append(centre);

    this.bindPointer();
  }

  // ------------------------------------------------------------------------------ open / close

  get open(): boolean {
    return this.opened;
  }

  toggle(): void {
    if (this.opened) this.close();
    else this.openPanel();
  }

  openPanel(): void {
    if (this.opened) return;
    this.opened = true;
    this.root.classList.add('on');
    // The map is the screen while it is up. Without this the tracker, the chat log and the raid
    // bar ghost through the scrim and compete with the thing you opened to read.
    document.body.classList.add('zonemap');
    this.readInk();
    this.loadPlate();
    // Every open starts fitted and centred: a map you left zoomed into the pylon field last time
    // is a map that does not answer "where am I" on the frame it appears.
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    // The readout is written by `applyView`, which a reset does not go through — without this it
    // would still say whatever the last session zoomed to.
    this.zoomEl.textContent = `${this.zoom.toFixed(1)}x`;
    this.pickedDeck = null;
    this.redrawAcc = 0;
    this.dirty = true;
    this.hideTip();
    this.redraw();
    this.host.onToggle(true);
  }

  close(): void {
    if (!this.opened) return;
    this.opened = false;
    this.root.classList.remove('on');
    document.body.classList.remove('zonemap');
    this.pointers.clear();
    this.dragFrom = null;
    this.stage.classList.remove('dragging');
    this.hideTip();
    this.host.onToggle(false);
  }

  /** Leaving the raid, dying into the hub, a fresh Welcome. Never leaves the panel behind. */
  clear(): void {
    this.close();
    this.map = null;
    this.zoneRect = SPINE_ZONE;
    this.areas = SPINE_ZONE_AREAS;
    this.hasPlate = false;
    this.plateUrl = null;
    this.deckBoxes = [[], [], [], []];
    this.exits = [];
    this.questArea = null;
    this.handIn = null;
    this.bag = null;
    this.roam = null;
    this.forced = null;
  }

  // ------------------------------------------------------------------------------- pushed state

  /**
   * The map being played. Bins the kit boxes by deck once, here, rather than filtering ~1500
   * AABBs ten times a second: region boxes (rocks, pylons, glass — the GLB's own dressing) are
   * scenery and never part of a deck plan, and the plate already shows them.
   */
  setMap(map: MapDef | null): void {
    this.map = map;
    this.deckBoxes = [[], [], [], []];
    this.exits = [];
    this.questArea = null;
    this.handIn = null;
    this.bag = null;
    this.roam = null;
    // A map change — including the seamless Lanternfall <-> Vigil Downs hand-off, which never
    // closes the panel — must never leave the previous zone's plate sitting under the new zone's
    // markers, or its pan/zoom framing the wrong rect. Resetting view state here, rather than only
    // at the next `openPanel`, is what makes that hand-off clean.
    this.zoom = 1;
    this.panX = 0;
    this.panY = 0;
    this.zoomEl.textContent = `${this.zoom.toFixed(1)}x`;
    this.pickedDeck = null;
    this.interior = false;
    if (!map) {
      this.zoneRect = SPINE_ZONE;
      this.areas = SPINE_ZONE_AREAS;
      this.hasPlate = false;
      this.plateUrl = null;
      this.dirty = true;
      return;
    }
    const zm = zoneMapFor(map.id);
    this.hasPlate = zm !== null && zm.plate !== null;
    this.zoneRect = this.hasPlate && zm ? zm.rect : boundsOf(map);
    this.areas = zm?.areas ?? [];
    this.plateUrl = this.hasPlate && zm ? zm.plate : null;
    this.loadPlate();
    this.sashEl.textContent = map.name.toUpperCase();

    // The deck plan is the wreck's own interior view — binning boxes into SPINE_DECKS bands is
    // Spine-only by nature, not just "has a plate": Lanternfall and the Downs have plates too now,
    // but neither has decks.
    if (map.id === 'spine') {
      const region = new Set<Aabb>(map.regionBoxes ?? []);
      for (const b of map.world.boxes) {
        if (region.has(b)) continue;
        if (b.max.x < SPINE_APRON_RECT.x0 || b.min.x > SPINE_APRON_RECT.x1) continue;
        if (b.max.z < SPINE_APRON_RECT.z0 || b.min.z > SPINE_APRON_RECT.z1) continue;
        for (let d = 0; d < SPINE_DECKS.length; d++) {
          const deck = SPINE_DECKS[d];
          if (!deck) continue;
          // A box is on this plan if it stands *between* the floor and the ceiling: the slabs
          // themselves are the room, not the furniture, and printing them would fill the whole
          // footprint with one rectangle. A box may of course stand on two decks at once.
          if (
            b.max.y > deck.floorY + SPINE_DECK_SLAB_M &&
            b.min.y < deck.headY - SPINE_DECK_SLAB_M
          ) {
            this.deckBoxes[d]?.push(b);
          }
        }
      }
    }
    this.dirty = true;
  }

  /**
   * The live extracts, off the last `ExtractSnap` list.
   *
   * That list only carries the ones the server has opened, but a map is a map: it shows every
   * way out this ground has, with the two that are lit in mint and the rest cold. So the map's own
   * `extracts` are the list and the snapshot only decides which are lit — plus anything the
   * snapshot names that the map does not (the `?debug` beacons), which is appended.
   */
  setExtracts(list: readonly ExtractSnap[]): void {
    const defs = this.map?.extracts ?? [];
    const live = new Map<number, ExtractSnap>();
    for (const x of list) live.set(x.id, x);
    const out: ZoneExit[] = defs.map((d) => ({
      id: d.id,
      name: d.name,
      x: d.pos.x,
      y: d.pos.y,
      z: d.pos.z,
      active: live.get(d.id)?.active ?? false,
    }));
    for (const x of list) {
      if (defs.some((d) => d.id === x.id)) continue;
      out.push({
        id: x.id,
        name: `exit ${String(x.id)}`,
        x: x.pos.x,
        y: x.pos.y,
        z: x.pos.z,
        active: x.active,
      });
    }
    this.exits = out;
    this.dirty = true;
  }

  /** The quest ring and the person a finished rung is waiting on. Either may be null. */
  setQuest(area: ZoneQuestArea | null, handIn: ZoneHandIn | null): void {
    this.questArea = area;
    this.handIn = handIn;
    this.dirty = true;
  }

  /** Where you died this run, if your bag is still on the ground. `null` clears it. */
  setBag(pos: Vec3 | null): void {
    this.bag = pos === null ? null : { x: pos.x, y: pos.y, z: pos.z };
    this.dirty = true;
  }

  /**
   * Every unattributed room line. Only one of them means anything here — the Verger's — and the
   * shared parser is what decides that, so a line about anything else falls straight through.
   */
  noteSystemLine(text: string): void {
    const region = roamRegionForNotice(text);
    if (!region) return;
    this.roam = region;
    this.dirty = true;
  }

  /** Screenshot hook, in the spirit of `Hud.forceHaloState`: stand the reader anywhere. */
  forcePose(pose: { pos: Vec3; yaw: number } | null): void {
    this.forced = pose;
    this.dirty = true;
  }

  // -------------------------------------------------------------------------------- frame loop

  /** Called every frame. A frame that is not a redraw frame touches no DOM and no canvas. */
  update(dtMs: number): void {
    if (!this.opened) return;
    this.redrawAcc += dtMs;
    if (this.redrawAcc < REDRAW_MS && !this.dirty) return;
    this.redrawAcc = 0;
    this.redraw();
  }

  // ------------------------------------------------------------------------------------ input

  private bindPointer(): void {
    // Wheel: zoom about the cursor. The game's own wheel handler needs the pointer lock, which is
    // released for the whole time a panel is up, so this can never also swap a weapon.
    this.stage.addEventListener(
      'wheel',
      (ev) => {
        ev.preventDefault();
        const p = this.local(ev.clientX, ev.clientY);
        const step = ev.deltaY < 0 ? 1.18 : 1 / 1.18;
        this.applyView(zoneZoomAbout(this.view(), this.zoom * step, p.x, p.y));
      },
      { passive: false },
    );

    this.stage.addEventListener('pointerdown', (ev) => {
      ev.preventDefault();
      this.stage.setPointerCapture(ev.pointerId);
      this.pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
      if (this.pointers.size === 2) {
        this.dragFrom = null;
        this.pinchDist = this.spread();
        this.pinchZoom = this.zoom;
        return;
      }
      const p = this.local(ev.clientX, ev.clientY);
      this.dragFrom = { x: p.x, y: p.y, panX: this.panX, panY: this.panY };
      this.stage.classList.add('dragging');
    });

    this.stage.addEventListener('pointermove', (ev) => {
      const held = this.pointers.get(ev.pointerId);
      if (held) {
        held.x = ev.clientX;
        held.y = ev.clientY;
      }
      const p = this.local(ev.clientX, ev.clientY);
      this.hoverX = p.x;
      this.hoverY = p.y;

      if (this.pointers.size >= 2) {
        // Pinch about the midpoint of the two fingers, so the map grows out of what you are
        // pointing at rather than out of the middle of the panel.
        const dist = this.spread();
        if (this.pinchDist > 0 && dist > 0) {
          const mid = this.midpoint();
          this.applyView(
            zoneZoomAbout(this.view(), (this.pinchZoom * dist) / this.pinchDist, mid.x, mid.y),
          );
        }
        this.hideTip();
        return;
      }
      if (this.dragFrom) {
        this.applyView({
          ...this.view(),
          panX: this.dragFrom.panX + (p.x - this.dragFrom.x),
          panY: this.dragFrom.panY + (p.y - this.dragFrom.y),
        });
        this.hideTip();
        return;
      }
      this.showTipAt(p.x, p.y);
    });

    const release = (ev: PointerEvent): void => {
      this.pointers.delete(ev.pointerId);
      if (this.pointers.size < 2) this.pinchDist = 0;
      if (this.pointers.size === 0) {
        this.dragFrom = null;
        this.stage.classList.remove('dragging');
      }
    };
    this.stage.addEventListener('pointerup', release);
    this.stage.addEventListener('pointercancel', release);
    this.stage.addEventListener('pointerleave', () => {
      this.hoverX = -1;
      this.hoverY = -1;
      this.hideTip();
    });
  }

  /** Pointer position in stage-local CSS px. */
  private local(clientX: number, clientY: number): { x: number; y: number } {
    const r = this.stage.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top };
  }

  private spread(): number {
    const [a, b] = [...this.pointers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  private midpoint(): { x: number; y: number } {
    const [a, b] = [...this.pointers.values()];
    if (!a || !b) return { x: this.stageW / 2, y: this.stageH / 2 };
    return this.local((a.x + b.x) / 2, (a.y + b.y) / 2);
  }

  private applyView(next: ZoneView): void {
    const clamped = clampZonePan(next);
    this.zoom = next.zoom;
    this.panX = clamped.panX;
    this.panY = clamped.panY;
    this.dirty = true;
    this.zoomEl.textContent = `${this.zoom.toFixed(1)}x`;
  }

  /**
   * Puts the player under the middle of the stage.
   *
   * At 1x the plate already fits, so the pan is pinned to zero and centring is a no-op — a button
   * that does nothing in the state the panel always opens in is worse than no button. So from a
   * fitted view it zooms to CENTRE_ZOOM on the player instead, which is what "centre on me" means
   * to somebody looking at a 268 m zone: show me where I am.
   */
  private centreOnMe(): void {
    const me = this.pose();
    if (!me) return;
    const zoom = this.zoom > 1 ? this.zoom : CENTRE_ZOOM;
    const unpanned = { ...this.view(), zoom, panX: 0, panY: 0 };
    const at = zoneToMap(me.pos.x, me.pos.z, unpanned);
    this.applyView({
      ...unpanned,
      panX: this.stageW / 2 - at.x,
      panY: this.stageH / 2 - at.y,
    });
  }

  // ------------------------------------------------------------------------------------ paint

  private pose(): { pos: Vec3; yaw: number } | null {
    return this.forced ?? this.host.viewer();
  }

  private view(): ZoneView {
    return {
      w: this.stageW,
      h: this.stageH,
      rect: this.interior ? DECK_VIEW_RECT : this.zoneRect,
      zoom: this.zoom,
      panX: this.panX,
      panY: this.panY,
    };
  }

  private readInk(): void {
    const cs = getComputedStyle(document.documentElement);
    const pick = (name: string, fallback: string): string =>
      cs.getPropertyValue(name).trim() || fallback;
    this.ink = {
      plum: pick('--plum', FALLBACK_INK.plum),
      wine: pick('--wine', FALLBACK_INK.wine),
      moon: pick('--moon', FALLBACK_INK.moon),
      lilac: pick('--lilac', FALLBACK_INK.lilac),
      lilacDim: pick('--lilac-dim', FALLBACK_INK.lilacDim),
      mint: pick('--mint', FALLBACK_INK.mint),
      cyan: pick('--cyan', FALLBACK_INK.cyan),
      magenta: pick('--magenta', FALLBACK_INK.magenta),
      red: pick('--red', FALLBACK_INK.red),
      rule: pick('--rule', FALLBACK_INK.rule),
    };
  }

  /**
   * Fetches the current map's plate, once per URL ever seen. Called from `setMap` (so a map
   * change starts the fetch even if the panel is already open, for the seamless Lanternfall <->
   * Downs hand-off) and again from `openPanel` as a harmless no-op guard. A map with no plate, or
   * one already cached (loaded or failed), does nothing. A missing file leaves the grid and the
   * markers — and warns exactly once, because the cache entry is created before the request goes
   * out and never re-created for the same URL.
   */
  private loadPlate(): void {
    const url = this.plateUrl;
    if (!url || this.plates.has(url)) return;
    const img = new Image();
    const entry: ZonePlate = { img, ready: false };
    this.plates.set(url, entry);
    img.decoding = 'async';
    img.addEventListener('load', () => {
      entry.ready = true;
      this.dirty = true;
    });
    img.addEventListener('error', () => {
      // Not a failure worth shouting about: the map still works, it just has no ground under it.
      console.warn('[stillsun] no zone map plate at', url);
    });
    img.src = url;
  }

  private redraw(): void {
    const ctx = this.ctx;
    if (!ctx) return;
    this.dirty = false;

    const w = Math.round(this.stage.clientWidth);
    const h = Math.round(this.stage.clientHeight);
    if (w <= 0 || h <= 0) return;
    const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
    if (w !== this.stageW || h !== this.stageH || dpr !== this.dpr) {
      this.stageW = w;
      this.stageH = h;
      this.dpr = dpr;
      this.canvas.width = Math.round(w * dpr);
      this.canvas.height = Math.round(h * dpr);
      // A resize can leave a pan that is no longer legal (the stage grew past the plate).
      const clamped = clampZonePan(this.view());
      this.panX = clamped.panX;
      this.panY = clamped.panY;
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const me = this.pose();
    const wasInterior = this.interior;
    // The deck plan is the wreck's own interior view — gated on the Spine by name, not on
    // "has a plate", now that Lanternfall and the Downs have plates of their own too.
    this.interior = this.map?.id === 'spine' && me !== null && inSpineHull(me.pos.x, me.pos.z);
    if (this.interior !== wasInterior) {
      // Crossing the breach re-fits the view: the two rects are 268 m and 73 m across, and a pan
      // that made sense on one is meaningless on the other.
      this.panX = 0;
      this.panY = 0;
      this.pickedDeck = null;
    }
    this.shownDeck = this.pickedDeck ?? (me ? spineDeckAt(me.pos.y) : 0);
    this.paintDeckStrip();
    this.paintSub();

    const view = this.view();
    this.fit = zoneFit(view);
    this.hitCount = 0;

    ctx.fillStyle = this.ink.plum;
    ctx.fillRect(0, 0, w, h);
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, w, h);
    ctx.clip();

    this.drawPlate(ctx, view);
    this.drawGrid(ctx, view);
    if (this.interior) this.drawDeckPlan(ctx, view);
    else this.drawRoam(ctx, view);
    if (!this.interior) this.drawAreaNames(ctx, view);
    this.drawQuest(ctx, view);
    this.drawExits(ctx, view);
    this.drawNpcs(ctx, view);
    this.drawHymns(ctx, view, me);
    this.drawAllies(ctx, view);
    this.drawBag(ctx, view);
    this.drawMe(ctx, view, me);

    ctx.restore();

    // The pointer has not moved, but what is under it may have.
    if (this.hoverX >= 0) this.showTipAt(this.hoverX, this.hoverY);
  }

  private drawPlate(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    if (!this.hasPlate || !this.plateUrl) return;
    const entry = this.plates.get(this.plateUrl);
    if (!entry || !entry.ready) return;
    const img = entry.img;
    // Placed by the CURRENT transform over the plate's own rect (the active map's, not the
    // Spine's), so the interior view — Spine-only — sees the right patch of it without a second
    // fit.
    const a = zoneToMap(this.zoneRect.x0, this.zoneRect.z0, view);
    const b = zoneToMap(this.zoneRect.x1, this.zoneRect.z1, view);
    ctx.save();
    // The plate ships neutral (a Workbench render of the real terrain); the Requiem grade lives
    // here so the asset stays the truth and the skin stays the skin.
    ctx.filter = 'saturate(0.4) brightness(0.6) contrast(1.12)';
    ctx.globalAlpha = this.interior ? 0.35 : 0.86;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, a.x, a.y, b.x - a.x, b.y - a.y);
    ctx.restore();
  }

  private drawGrid(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    const fit = zoneFit(view);
    if (fit.scale <= 0) return;
    ctx.save();
    ctx.strokeStyle = this.ink.rule;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const first = Math.ceil(view.rect.x0 / ZONE_GRID_M) * ZONE_GRID_M;
    for (let x = first; x <= view.rect.x1; x += ZONE_GRID_M) {
      const px = Math.round(zoneToMap(x, 0, view).x) + 0.5;
      ctx.moveTo(px, fit.y);
      ctx.lineTo(px, fit.y + fit.h);
    }
    const firstZ = Math.ceil(view.rect.z0 / ZONE_GRID_M) * ZONE_GRID_M;
    for (let z = firstZ; z <= view.rect.z1; z += ZONE_GRID_M) {
      const py = Math.round(zoneToMap(0, z, view).y) + 0.5;
      ctx.moveTo(fit.x, py);
      ctx.lineTo(fit.x + fit.w, py);
    }
    ctx.stroke();
    // The edge of the world, so a plate that runs off the panel still has a border.
    ctx.strokeStyle = this.ink.wine;
    ctx.strokeRect(
      Math.round(fit.x) + 0.5,
      Math.round(fit.y) + 0.5,
      Math.round(fit.w),
      Math.round(fit.h),
    );
    ctx.restore();
  }

  private drawAreaNames(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    const size = Math.max(11, Math.min(20, 12 * view.zoom));
    ctx.save();
    ctx.font = `${size.toFixed(0)}px ${DISPLAY_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    setTracking(ctx, '0.2em');
    ctx.lineJoin = 'round';
    for (const area of this.areas) {
      const p = zoneToMap(area.x, area.z, view);
      if (!this.onPlate(p)) continue;
      const label = area.name.toUpperCase();
      // Pulled back inside the plate rather than allowed to run off it: the Glass Outcrop and the
      // Pylon Field are both authored near the edge of the zone, and half a name is not a name.
      const half = ctx.measureText(label).width / 2 + 3;
      const x = Math.min(
        Math.max(p.x, this.fit.x + half),
        Math.max(this.fit.x + half, this.fit.x + this.fit.w - half),
      );
      // The one legibility treatment the skin allows over a picture: a plum outline, no glow.
      ctx.strokeStyle = this.ink.plum;
      ctx.lineWidth = 3;
      ctx.strokeText(label, x, p.y);
      ctx.fillStyle = this.ink.lilac;
      ctx.fillText(label, x, p.y);
    }
    setTracking(ctx, '0em');
    ctx.restore();
  }

  /**
   * The roaming Verger's announced ground: magenta hatching, no outline and no icon. It is a
   * rumour off one chat line — "it is walking the dry channel" — and hatching is exactly as
   * precise as that sentence.
   */
  private drawRoam(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    const r = this.roam;
    if (!r) return;
    const a = zoneToMap(r.x0, r.z0, view);
    const b = zoneToMap(r.x1, r.z1, view);
    ctx.save();
    ctx.beginPath();
    ctx.rect(a.x, a.y, b.x - a.x, b.y - a.y);
    ctx.clip();
    ctx.strokeStyle = this.ink.magenta;
    ctx.globalAlpha = 0.22;
    ctx.lineWidth = 1;
    ctx.beginPath();
    const step = 14;
    for (let d = a.y - (b.x - a.x); d < b.y + (b.x - a.x); d += step) {
      ctx.moveTo(a.x, d);
      ctx.lineTo(b.x, d + (b.x - a.x));
    }
    ctx.stroke();
    ctx.restore();
    this.hit((a.x + b.x) / 2, (a.y + b.y) / 2, `The Verger was announced walking ${r.walking}`);
  }

  /**
   * The deck plan: the kit's own AABBs for the band the tabs are showing, plus the routes between
   * decks. Drawn over a dimmed plate so the wreck still sits in its basin.
   */
  private drawDeckPlan(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    const boxes = this.deckBoxes[this.shownDeck] ?? [];
    ctx.save();
    // The deck itself, so an empty plan (the roof) is still a shape and a full one has a floor
    // under its walls rather than a photograph of the roof.
    const hullA = zoneToMap(SPINE_HULL_RECT.x0, SPINE_HULL_RECT.z0, view);
    const hullB = zoneToMap(SPINE_HULL_RECT.x1, SPINE_HULL_RECT.z1, view);
    ctx.fillStyle = this.ink.plum;
    ctx.globalAlpha = 0.72;
    ctx.fillRect(hullA.x, hullA.y, hullB.x - hullA.x, hullB.y - hullA.y);
    ctx.globalAlpha = 1;
    ctx.strokeStyle = this.ink.lilacDim;
    ctx.lineWidth = 1;
    ctx.strokeRect(hullA.x + 0.5, hullA.y + 0.5, hullB.x - hullA.x - 1, hullB.y - hullA.y - 1);

    ctx.fillStyle = this.ink.wine;
    ctx.strokeStyle = this.ink.lilac;
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.95;
    for (const b of boxes) {
      const a = zoneToMap(b.min.x, b.min.z, view);
      const c = zoneToMap(b.max.x, b.max.z, view);
      const w = Math.max(1, c.x - a.x);
      const h = Math.max(1, c.y - a.y);
      ctx.fillRect(a.x, a.y, w, h);
      if (w > 3 && h > 3) ctx.strokeRect(a.x + 0.5, a.y + 0.5, w - 1, h - 1);
    }
    ctx.globalAlpha = 1;

    ctx.font = `11px ${DISPLAY_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    setTracking(ctx, '0.16em');
    for (const f of DECK_FEATURES) {
      if (!f.decks.includes(this.shownDeck)) continue;
      const a = zoneToMap(f.rect.x0, f.rect.z0, view);
      const c = zoneToMap(f.rect.x1, f.rect.z1, view);
      ctx.strokeStyle = this.ink.mint;
      ctx.setLineDash([4, 3]);
      ctx.lineWidth = 1.5;
      ctx.strokeRect(a.x, a.y, c.x - a.x, c.y - a.y);
      ctx.setLineDash([]);
      const mx = (a.x + c.x) / 2;
      // At the foot of the box, not its middle: the Cargo Lift extract sits four metres off the
      // top edge of the climb, and two labels centred four metres apart are one smudge.
      const my = c.y - 4;
      ctx.strokeStyle = this.ink.plum;
      ctx.lineWidth = 3;
      ctx.lineJoin = 'round';
      ctx.strokeText(f.label, mx, my);
      ctx.fillStyle = this.ink.mint;
      ctx.fillText(f.label, mx, my);
      this.hit(mx, my, f.tip);
    }
    setTracking(ctx, '0em');
    ctx.restore();
  }

  private drawQuest(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    const area = this.questArea;
    if (!area) return;
    if (!this.onDeck(area.y)) return;
    const p = zoneToMap(area.x, area.z, view);
    if (!this.onPlate(p)) return;
    const r = Math.max(6, area.radius * this.fit.scale);
    ctx.save();
    ctx.strokeStyle = this.ink.mint;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([5, 4]);
    ctx.beginPath();
    ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.font = `12px ${DISPLAY_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    setTracking(ctx, '0.16em');
    const label = area.name.toUpperCase();
    ctx.strokeStyle = this.ink.plum;
    ctx.lineWidth = 3;
    ctx.lineJoin = 'round';
    ctx.strokeText(label, p.x, p.y - r - 5);
    ctx.fillStyle = this.ink.mint;
    ctx.fillText(label, p.x, p.y - r - 5);
    setTracking(ctx, '0em');
    ctx.restore();
    this.hit(p.x, p.y, `Objective — ${area.name}`);
  }

  private drawExits(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    for (const exit of this.exits) {
      if (!this.onDeck(exit.y)) continue;
      const p = zoneToMap(exit.x, exit.z, view);
      if (!this.onPlate(p)) continue;
      const colour = exit.active ? this.ink.mint : this.ink.lilacDim;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 1.6;
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      // The compass's own extract glyph: a door on the right, and a way out through it.
      ctx.beginPath();
      ctx.moveTo(1, -7);
      ctx.lineTo(7, -7);
      ctx.lineTo(7, 7);
      ctx.lineTo(1, 7);
      ctx.moveTo(-7, 0);
      ctx.lineTo(4, 0);
      ctx.moveTo(0, -4);
      ctx.lineTo(4, 0);
      ctx.lineTo(0, 4);
      ctx.stroke();
      ctx.restore();
      this.label(ctx, exit.name.toUpperCase(), p.x, p.y + 9, colour, 'top');
      this.hit(p.x, p.y, exit.active ? `${exit.name} — lit` : `${exit.name} — cold`);
    }
  }

  /** The people standing on the map, and the one a finished rung is waiting on. */
  private drawNpcs(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    for (const npc of this.map?.npcs ?? []) {
      if (!this.onDeck(npc.pos.y)) continue;
      const p = zoneToMap(npc.pos.x, npc.pos.z, view);
      if (!this.onPlate(p)) continue;
      this.lantern(ctx, p.x, p.y, 4, this.ink.lilacDim);
      this.hit(p.x, p.y, `${npc.name} — ${npc.role}`);
    }
    const handIn = this.handIn;
    if (!handIn) return;
    if (!this.onDeck(handIn.y)) return;
    const p = zoneToMap(handIn.x, handIn.z, view);
    if (!this.onPlate(p)) return;
    this.lantern(ctx, p.x, p.y, 7, this.ink.mint);
    this.label(ctx, `RETURN TO ${handIn.name.toUpperCase()}`, p.x, p.y + 11, this.ink.mint, 'top');
    this.hit(p.x, p.y, `Return to ${handIn.name}`);
  }

  private drawHymns(
    ctx: CanvasRenderingContext2D,
    view: ZoneView,
    me: { pos: Vec3; yaw: number } | null,
  ): void {
    if (!me) return;
    const n = this.host.hymns(this.hymnBuf);
    ctx.save();
    ctx.fillStyle = this.ink.magenta;
    for (let i = 0; i < n; i++) {
      const h = this.hymnBuf[i];
      if (!h || h.friendly) continue;
      if (Math.hypot(h.pos.x - me.pos.x, h.pos.z - me.pos.z) > HYMN_RANGE_M) continue;
      if (!this.onDeck(h.pos.y)) continue;
      const p = zoneToMap(h.pos.x, h.pos.z, view);
      if (!this.onPlate(p)) continue;
      ctx.beginPath();
      ctx.moveTo(p.x, p.y - 4.5);
      ctx.lineTo(p.x + 4.5, p.y);
      ctx.lineTo(p.x, p.y + 4.5);
      ctx.lineTo(p.x - 4.5, p.y);
      ctx.closePath();
      ctx.fill();
      this.hit(p.x, p.y, h.height > 3 ? 'Verger' : 'Hymn');
    }
    ctx.restore();
  }

  private drawAllies(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    const n = this.host.allies(this.allyBuf);
    for (let i = 0; i < n; i++) {
      const a = this.allyBuf[i];
      if (!a) continue;
      if (!this.onDeck(a.pos.y)) continue;
      const p = zoneToMap(a.pos.x, a.pos.z, view);
      if (!this.onPlate(p)) continue;
      ctx.save();
      ctx.fillStyle = this.ink.cyan;
      ctx.beginPath();
      ctx.arc(p.x, p.y, 4, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      const name = this.host.allyName(a.id);
      this.label(ctx, name, p.x, p.y + 7, this.ink.cyan, 'top');
      this.hit(p.x, p.y, name);
    }
  }

  private drawBag(ctx: CanvasRenderingContext2D, view: ZoneView): void {
    const bag = this.bag;
    if (!bag) return;
    if (!this.onDeck(bag.y)) return;
    const p = zoneToMap(bag.x, bag.z, view);
    if (!this.onPlate(p)) return;
    ctx.save();
    ctx.strokeStyle = this.ink.red;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(p.x - 4, p.y - 4);
    ctx.lineTo(p.x + 4, p.y + 4);
    ctx.moveTo(p.x + 4, p.y - 4);
    ctx.lineTo(p.x - 4, p.y + 4);
    ctx.stroke();
    ctx.restore();
    this.hit(p.x, p.y, 'Your bag — where you went down');
  }

  private drawMe(
    ctx: CanvasRenderingContext2D,
    view: ZoneView,
    me: { pos: Vec3; yaw: number } | null,
  ): void {
    if (!me) return;
    const p = zoneToMap(me.pos.x, me.pos.z, view);
    if (!this.onPlate(p)) return;
    // yaw 0 faces -Z and positive yaw turns left, so camera forward is (-sin, -cos) in world XZ —
    // and on the map +x is right and +z is down, which is the same pair of numbers.
    const fx = -Math.sin(me.yaw);
    const fz = -Math.cos(me.yaw);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(Math.atan2(fx, -fz));
    ctx.fillStyle = this.ink.mint;
    ctx.strokeStyle = this.ink.plum;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(0, -9);
    ctx.lineTo(6.5, 7);
    ctx.lineTo(0, 3.5);
    ctx.lineTo(-6.5, 7);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.restore();
    this.hit(p.x, p.y, 'You');
  }

  // ------------------------------------------------------------------------------ paint helpers

  /** A small lantern: a shade over a lit body. The map's mark for a person you can talk to. */
  private lantern(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
    colour: string,
  ): void {
    ctx.save();
    ctx.translate(x, y);
    ctx.strokeStyle = colour;
    ctx.fillStyle = colour;
    ctx.lineWidth = 1.4;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(-r * 0.7, -r * 0.4);
    ctx.lineTo(r * 0.7, -r * 0.4);
    ctx.lineTo(r * 0.5, r);
    ctx.lineTo(-r * 0.5, r);
    ctx.closePath();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(0, -r);
    ctx.lineTo(0, -r * 0.4);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(0, r * 0.25, Math.max(1, r * 0.28), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private label(
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    colour: string,
    baseline: CanvasTextBaseline,
  ): void {
    ctx.save();
    ctx.font = `11px ${DISPLAY_FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = baseline;
    setTracking(ctx, '0.14em');
    ctx.lineJoin = 'round';
    ctx.strokeStyle = this.ink.plum;
    ctx.lineWidth = 3;
    ctx.strokeText(text, x, y);
    ctx.fillStyle = colour;
    ctx.fillText(text, x, y);
    setTracking(ctx, '0em');
    ctx.restore();
  }

  /**
   * Is this thing on the deck the plan is showing?
   *
   * Only asked in the interior view. Outdoors the whole zone is one surface and a height means
   * nothing; inside the wreck, three decks share one 50 x 28 m footprint, and a Chorister two
   * floors below you drawn on your plan is worse than no mark at all.
   */
  private onDeck(y: number): boolean {
    return !this.interior || spineDeckAt(y) === this.shownDeck;
  }

  /**
   * Is this pixel on the ground the map is currently showing?
   *
   * Not the same question as "is it on the canvas": in the interior view the fitted rect is the
   * hull and its apron, and a marker 40 m out on the dunes would otherwise be drawn in the
   * letterbox beside the plan, where it means nothing and sits over the deck tabs.
   */
  private onPlate(p: { x: number; y: number }): boolean {
    const f = this.fit;
    return p.x >= f.x - 2 && p.x <= f.x + f.w + 2 && p.y >= f.y - 2 && p.y <= f.y + f.h + 2;
  }

  /** Records a hoverable point. The array is grown once and reused for the rest of the session. */
  private hit(x: number, y: number, text: string): void {
    const slot = this.hits[this.hitCount];
    if (slot) {
      slot.x = x;
      slot.y = y;
      slot.text = text;
    } else {
      this.hits.push({ x, y, text });
    }
    this.hitCount += 1;
  }

  // --------------------------------------------------------------------------------- chrome

  private paintSub(): void {
    const zone = this.interior
      ? (SPINE_DECKS[this.shownDeck]?.name ?? '')
      : this.map?.id === 'spine'
        ? 'THE BAND'
        : '';
    if (zone === this.lastSub) return;
    this.lastSub = zone;
    this.subEl.textContent = zone;
  }

  private paintDeckStrip(): void {
    this.deckStrip.classList.toggle('on', this.interior);
    if (!this.interior) return;
    const me = this.pose();
    const here = me ? spineDeckAt(me.pos.y) : -1;
    this.deckBtns.forEach((btn, i) => {
      btn.classList.toggle('on', i === this.shownDeck);
      btn.classList.toggle('here', i === here && i !== this.shownDeck);
    });
  }

  private showTipAt(x: number, y: number): void {
    let best: ZoneHit | null = null;
    let bestD = HOVER_R * HOVER_R;
    for (let i = 0; i < this.hitCount; i++) {
      const h = this.hits[i];
      if (!h) continue;
      const d = (h.x - x) * (h.x - x) + (h.y - y) * (h.y - y);
      if (d <= bestD) {
        bestD = d;
        best = h;
      }
    }
    if (!best) {
      this.hideTip();
      return;
    }
    this.tipEl.textContent = best.text;
    this.tipEl.classList.add('on');
    // Flip the tip back inside the stage rather than letting it clip on the right-hand edge.
    const w = this.tipEl.offsetWidth;
    const h = this.tipEl.offsetHeight;
    const left = x + 14 + w > this.stageW ? x - 14 - w : x + 14;
    const top = y + 12 + h > this.stageH ? y - 12 - h : y + 12;
    this.tipEl.style.left = `${String(Math.max(0, Math.round(left)))}px`;
    this.tipEl.style.top = `${String(Math.max(0, Math.round(top)))}px`;
  }

  private hideTip(): void {
    this.tipEl.classList.remove('on');
  }
}

/** Bebas, with the same fallbacks index.html's --display token carries. */
const DISPLAY_FONT = "'Bebas Neue', Impact, 'Arial Narrow', sans-serif";

/**
 * Canvas letter-spacing. Part of the 2D context in Chromium and Firefox, absent in older Safari —
 * and a tracked Bebas cap is the skin's voice, not a decoration, so it is worth the feature test
 * (which costs one property write and degrades to plain caps).
 */
function setTracking(ctx: CanvasRenderingContext2D, value: string): void {
  const c = ctx as CanvasRenderingContext2D & { letterSpacing?: string };
  if (typeof c.letterSpacing === 'string') c.letterSpacing = value;
}

/**
 * A rect around everything a map has in it, for the maps with no plate (the Yard). Padded, and
 * never degenerate: a map with one box would otherwise fit at an infinite scale.
 */
function boundsOf(map: MapDef): ZoneRect {
  let x0 = Infinity;
  let x1 = -Infinity;
  let z0 = Infinity;
  let z1 = -Infinity;
  const eat = (x: number, z: number): void => {
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (z < z0) z0 = z;
    if (z > z1) z1 = z;
  };
  for (const b of map.world.boxes) {
    eat(b.min.x, b.min.z);
    eat(b.max.x, b.max.z);
  }
  for (const s of map.spawns) eat(s.x, s.z);
  for (const e of map.extracts ?? []) eat(e.pos.x, e.pos.z);
  for (const n of map.npcs ?? []) eat(n.pos.x, n.pos.z);
  if (!Number.isFinite(x0)) return { x0: -30, x1: 30, z0: -30, z1: 30 };
  const pad = 8;
  return { x0: x0 - pad, x1: x1 + pad, z0: z0 - pad, z1: z1 + pad };
}
